export const BLOCKLY_TEST_LEANFS_TYPE = Object.freeze({
  DIR: 'dir',
  BLOCKLY: 'blockly',
  PYTHON: 'python',
  OTHERS: 'others',
});

export type BlocklyTestLeanFsType =
  typeof BLOCKLY_TEST_LEANFS_TYPE[keyof typeof BLOCKLY_TEST_LEANFS_TYPE];

export interface BlocklyTestLeanFsItem {
  index: string;
  isFolder: boolean;
  children: string[] | null;
  data: string;
  parent: string | null;
}

type StoredLeanFsItem = {
  data: string;
  children: string[] | null;
};

export class BlocklyTestLeanFs {
  private static readonly FS_VER = 'BlocklyTestLeanFs10';
  private static readonly LS_KEY_TREE = `${BlocklyTestLeanFs.FS_VER}__tree`;
  private static readonly FILE_KEY_PREFIX = `${BlocklyTestLeanFs.FS_VER}_`;
  private static readonly ROOT_UUID = 'd27adeb0-c485-49b9-a9de-b547b23b08e7';
  private static readonly LOCAL_CONFIG_UUID = '65b04c42-fb73-4963-9680-4781e385244f';

  private items: Record<string, BlocklyTestLeanFsItem> = {};
  private delaySaveTree = false;

  async mount() {
    await this.loadTree();
  }

  getItems() {
    return this.items;
  }

  getRoot() {
    return BlocklyTestLeanFs.ROOT_UUID;
  }

  getLocalConfigUUID() {
    return BlocklyTestLeanFs.LOCAL_CONFIG_UUID;
  }

  isExist(itemUUID: string | null | undefined) {
    return !!itemUUID && !!this.items[itemUUID];
  }

  isFile(itemUUID: string | null | undefined) {
    if (!itemUUID) return false;
    const item = this.items[itemUUID];
    return !!item && item.isFolder === false;
  }

  isDir(itemUUID: string | null | undefined) {
    if (!itemUUID) return false;
    const item = this.items[itemUUID];
    return !!item && item.isFolder === true;
  }

  getParent(itemUUID: string | null | undefined) {
    if (!itemUUID) return null;
    const parent = this.items[itemUUID]?.parent;
    return parent && this.items[parent] ? parent : null;
  }

  getAllChildren(itemUUID: string | null | undefined) {
    if (!itemUUID || !this.isDir(itemUUID)) return [];
    return (this.items[itemUUID].children || []).filter(childId => !!this.items[childId]);
  }

  getName(itemUUID: string | null | undefined) {
    if (!itemUUID) return null;
    return this.items[itemUUID]?.data || null;
  }

  getItemType(itemUUID: string | null | undefined): BlocklyTestLeanFsType {
    if (!itemUUID || !this.isExist(itemUUID)) return BLOCKLY_TEST_LEANFS_TYPE.OTHERS;
    if (this.isDir(itemUUID)) return BLOCKLY_TEST_LEANFS_TYPE.DIR;

    const name = this.items[itemUUID].data || '';
    const lowerName = name.toLowerCase();
    if (lowerName.endsWith('.bduino')) return BLOCKLY_TEST_LEANFS_TYPE.BLOCKLY;
    if (lowerName.endsWith('.py')) return BLOCKLY_TEST_LEANFS_TYPE.PYTHON;

    return BLOCKLY_TEST_LEANFS_TYPE.OTHERS;
  }

  isType(itemUUID: string | null | undefined, type: BlocklyTestLeanFsType) {
    return this.getItemType(itemUUID) === type;
  }

  isBlocklyFile(itemUUID: string | null | undefined) {
    return this.isType(itemUUID, BLOCKLY_TEST_LEANFS_TYPE.BLOCKLY);
  }

  isPythonFile(itemUUID: string | null | undefined) {
    return this.isType(itemUUID, BLOCKLY_TEST_LEANFS_TYPE.PYTHON);
  }

  isEditableFile(itemUUID: string | null | undefined) {
    return this.isBlocklyFile(itemUUID) || this.isPythonFile(itemUUID);
  }

  async createFile(parentUUID: string) {
    const childUUID = await this.createItem(parentUUID);
    if (!childUUID) return null;

    this.items[childUUID].isFolder = false;
    this.items[childUUID].children = null;
    await this.saveTree();

    return childUUID;
  }

  async createDir(parentUUID: string) {
    const childUUID = await this.createItem(parentUUID);
    if (!childUUID) return null;

    this.items[childUUID].isFolder = true;
    this.items[childUUID].children = [];
    await this.saveTree();

    return childUUID;
  }

  async rename(itemUUID: string, newName: string) {
    if (itemUUID === this.getRoot()) return;
    if (!this.isExist(itemUUID)) return;

    this.items[itemUUID].data = newName;
    await this.saveTree();
  }

  async readFile(itemUUID: string) {
    if (!this.isFile(itemUUID)) return null;
    return localStorage.getItem(this.fileKey(itemUUID));
  }

  async writeFile(itemUUID: string, content: string) {
    if (!this.isFile(itemUUID)) return;
    localStorage.setItem(this.fileKey(itemUUID), content ?? '');
  }

  async deleteFile(itemUUID: string) {
    if (itemUUID === this.getRoot()) return;
    if (!this.isFile(itemUUID)) return;

    this.removeFromParent(itemUUID);
    localStorage.removeItem(this.fileKey(itemUUID));
    delete this.items[itemUUID];
    await this.saveTree();
  }

  async deleteDir(itemUUID: string) {
    if (itemUUID === this.getRoot()) return;
    if (!this.isDir(itemUUID)) return;

    const previousDelaySaveTree = this.delaySaveTree;
    this.delaySaveTree = true;

    const children = [...this.getAllChildren(itemUUID)];
    for (const childId of children) {
      if (this.isDir(childId)) {
        await this.deleteDir(childId);
      } else {
        await this.deleteFile(childId);
      }
    }

    this.removeFromParent(itemUUID);
    delete this.items[itemUUID];

    this.delaySaveTree = previousDelaySaveTree;
    await this.saveTree();
  }

  getAncestorFolders(uuid: string | null | undefined) {
    const out: string[] = [];
    let parent = this.getParent(uuid);

    while (parent && parent !== this.getRoot()) {
      out.unshift(parent);
      parent = this.getParent(parent);
    }

    return out;
  }

  hasAnyFileOfType(type: BlocklyTestLeanFsType) {
    const stack = [this.getRoot()];

    while (stack.length) {
      const id = stack.pop();
      if (!id) continue;
      if (this.isType(id, type)) return true;
      if (this.isDir(id)) stack.push(...this.getAllChildren(id));
    }

    return false;
  }

  findFirstFileOfType(type: BlocklyTestLeanFsType) {
    const stack = [this.getRoot()];

    while (stack.length) {
      const id = stack.shift();
      if (!id) continue;
      if (this.isType(id, type)) return id;
      if (this.isDir(id)) stack.push(...this.getAllChildren(id));
    }

    return null;
  }

  pickNextItemAfterDelete(uuid: string) {
    const parent = this.getParent(uuid);
    if (parent && parent !== this.getRoot()) {
      return parent;
    }

    const children = this.getAllChildren(this.getRoot());
    const firstFile = children.find(childId => this.isFile(childId) && childId !== uuid);
    if (firstFile) return firstFile;

    return children.find(childId => childId !== uuid) || null;
  }

  async insertAtIndex(folderId: string, childId: string, atIndex: number) {
    if (!this.isDir(folderId) || !this.isExist(childId) || childId === this.getRoot()) {
      return null;
    }

    const removedParent = this.removeFromParent(childId);
    const folder = this.items[folderId];
    folder.children = (folder.children || []).filter(id => id !== childId);

    let index = Number.isFinite(atIndex) ? atIndex : folder.children.length;
    index = Math.max(0, Math.min(index, folder.children.length));

    folder.children.splice(index, 0, childId);
    this.items[childId].parent = folderId;

    await this.saveTree();
    return removedParent;
  }

  isAncestorOf(folderId: string, itemId: string | null | undefined) {
    if (!this.isDir(folderId) || !itemId) return false;
    return this.getAncestorFolders(itemId).includes(folderId);
  }

  private async createItem(parentId: string) {
    if (!this.isDir(parentId)) return null;

    const uuid = createUUID();
    this.items[uuid] = {
      index: uuid,
      isFolder: false,
      children: null,
      data: getTimestampName(),
      parent: parentId,
    };

    const parent = this.items[parentId];
    parent.children ||= [];
    parent.children.unshift(uuid);

    return uuid;
  }

  private async loadTree() {
    const rawTree = localStorage.getItem(BlocklyTestLeanFs.LS_KEY_TREE);
    if (!rawTree) {
      await this.newTree();
      return;
    }

    try {
      const storedItems = JSON.parse(rawTree) as Record<string, StoredLeanFsItem>;
      this.items = {};

      for (const [uuid, item] of Object.entries(storedItems)) {
        const isFolder = Array.isArray(item.children);
        this.items[uuid] = {
          index: uuid,
          isFolder,
          children: isFolder ? [...(item.children || [])] : null,
          data: typeof item.data === 'string' ? item.data : getTimestampName(),
          parent: null,
        };
      }

      if (!this.items[this.getRoot()]) {
        await this.newTree();
        return;
      }

      this.rebuildParents();
    } catch {
      await this.newTree();
    }
  }

  private async newTree() {
    this.items = {
      [this.getRoot()]: {
        index: this.getRoot(),
        isFolder: true,
        children: [],
        data: 'Workspace',
        parent: null,
      },
    };
    await this.saveTree();
  }

  private async saveTree() {
    if (this.delaySaveTree) return;

    const storedItems: Record<string, StoredLeanFsItem> = {};
    for (const [uuid, item] of Object.entries(this.items)) {
      storedItems[uuid] = {
        data: item.data,
        children: Array.isArray(item.children) ? [...item.children] : null,
      };
    }

    localStorage.setItem(BlocklyTestLeanFs.LS_KEY_TREE, JSON.stringify(storedItems));
  }

  private rebuildParents() {
    for (const item of Object.values(this.items)) {
      item.parent = null;
    }

    for (const [uuid, item] of Object.entries(this.items)) {
      if (!Array.isArray(item.children)) continue;
      item.children = item.children.filter(childId => !!this.items[childId]);
      for (const childId of item.children) {
        this.items[childId].parent = uuid;
      }
    }
  }

  private removeFromParent(childId: string) {
    let removedParent: string | null = null;

    const parentId = this.getParent(childId);
    const parent = parentId ? this.items[parentId] : null;
    if (parent?.children) {
      const before = parent.children.length;
      parent.children = parent.children.filter(id => id !== childId);
      if (parent.children.length !== before) removedParent = parentId;
    }

    for (const [uuid, item] of Object.entries(this.items)) {
      if (!Array.isArray(item.children)) continue;
      const before = item.children.length;
      item.children = item.children.filter(id => id !== childId);
      if (item.children.length !== before) removedParent ||= uuid;
    }

    return removedParent;
  }

  private fileKey(uuid: string) {
    return BlocklyTestLeanFs.FILE_KEY_PREFIX + uuid;
  }
}

function createUUID() {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getTimestampName() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');

  return `${yyyy}.${mm}.${dd}-${hh}.${mi}.${sec}`;
}
